import { type LocationDescriptor } from "history";
import { useCallback } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { BTButton, type BTButtonType } from "@btnet/core-components/BTButton";

import { formatRouteForNewTab } from "utilities/url/url";

import {
    BTButtonDeprecated,
    type IBTButtonDeprecatedProps,
} from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";

interface ILinkButtonPropsOptional {
    replace?: boolean;
    isolated?: boolean;
    underline?: boolean;
    noShadow?: boolean;

    to: string | LocationDescriptor;
    dontUseMatchUrl?: boolean;
    /**
     * If true will treat `to` as an external link.
     * @default false
     */
    isExternal?: boolean;

    /**
     * If true code will adjust passed link url and open in a new tab.
     * @default false
     */
    showInNewTab?: boolean;
}

type ILinkButtonProps<ActionType> = Omit<
    IBTButtonDeprecatedProps<ActionType>,
    "isolated" | "underline" | "noShadow" | "target" | "rel"
> &
    ILinkButtonPropsOptional & {
        useNewBTButtons?: boolean;
    };

const noop = () => {};

const linkButtonTypeMapping: Partial<Record<string, BTButtonType>> = {
    link: "tertiary",
    ghost: "tertiary",
    dashed: "tertiary",
    new: "primary",
    destruction: "danger",
    "marketing-primary": "brandprimary",
};

const oldToNewButtonType = (type?: string): BTButtonType => {
    if (type) {
        return linkButtonTypeMapping[type] ?? (type as BTButtonType);
    } else {
        return "secondary";
    }
};

/**
 * LinkButton utilizes the route in the `to` property to navigate to the
 * provided route when clicked. When `isExternal` is true it will handle
 * navigation to an external link.
 */
export function LinkButton<ActionType>(
    props: ILinkButtonProps<ActionType>,
): ReturnType<React.FunctionComponent<ILinkButtonProps<ActionType>>> {
    const {
        replace,
        to,
        showInNewTab,
        isExternal = false,
        dontUseMatchUrl,
        ...buttonProps
    } = props;
    const { onClick = noop } = buttonProps;

    const history = useHistory();
    const match = useRouteMatch();

    // click event ported from https://github.com/remix-run/react-router/blob/b529499efcb906c814b9a3a68e2b4292a15b09c8/packages/react-router-dom/modules/Link.js#L47
    const handleButtonClick = useCallback(
        (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<any, MouseEvent>) => {
            try {
                onClick(event as any);
            } catch (ex) {
                event.preventDefault();
                throw ex;
            }

            if (isExternal) {
                return;
            }

            const navigate = () => {
                if (showInNewTab && typeof to === "string") {
                    window.open(formatRouteForNewTab(to), "_blank");
                    return;
                }

                const toInternal = dontUseMatchUrl ? to : `${match.url}${to}`;
                const method: (location: LocationDescriptor, state?: unknown) => void = replace
                    ? history.replace
                    : history.push;

                method(toInternal, history.location.state);
            };

            // onClick prevented default
            if (!event.defaultPrevented) {
                event.preventDefault();
                navigate();
            }
        },
        [
            history.location.state,
            history.push,
            history.replace,
            isExternal,
            showInNewTab,
            match.url,
            onClick,
            replace,
            to,
            dontUseMatchUrl,
        ],
    );

    if (isExternal && typeof to === "string") {
        const handleClick = onClick as (event: React.MouseEvent<any, MouseEvent>) => void;
        return (
            <BTExternalLink
                href={to}
                data-testid={props["data-testid"]}
                isolated={props.isolated}
                disabled={props.disabled}
                onClick={handleClick}
                buttonType={props.type ?? "secondary"}
                className={props.className}
                hotkey={props.hotkey}
                icon={props.icon}
            >
                {props.children}
            </BTExternalLink>
        );
    }

    if (props.useNewBTButtons) {
        const buttonType = oldToNewButtonType(props.type);
        return (
            <BTButton<ActionType> {...buttonProps} type={buttonType} onClick={handleButtonClick} />
        );
    }

    return <BTButtonDeprecated<ActionType> {...buttonProps} onClick={handleButtonClick} />;
}
